import React, { useState, useEffect } from 'react';
import { Plus, Mail, MoreHorizontal, Search, ChevronLeft, ChevronRight, Calendar, Users, Send } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayoutOptimized';

interface Campaign {
  id: string;
  name: string;
  subject: string;
  status: 'draft' | 'scheduled' | 'sent' | 'paused';
  sent_count: number;
  open_count: number;
  click_count: number;
  scheduled_date: string | null;
  created_at: string;
  target_audience: string;
}

const CampaignsPage: React.FC = () => {
  // State
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [campaignsPerPage] = useState(10);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    subject: '',
    content: '',
    target_audience: 'all'
  });

  // Sample data (replace with actual API call)
  useEffect(() => {
    const fetchCampaigns = async () => {
      setIsLoading(true);
      try {
        // In a real app, this would be a database call
        // For now, we'll use sample data
        setCampaigns([
          {
            id: '1',
            name: 'Welcome Series',
            subject: 'Bienvenue chez Smart Impact',
            status: 'sent',
            sent_count: 245,
            open_count: 180,
            click_count: 75,
            scheduled_date: null,
            created_at: '2025-04-15T10:30:00Z',
            target_audience: 'Nouveaux prospects'
          },
          {
            id: '2',
            name: 'Monthly Newsletter',
            subject: 'Actualités Smart Impact - Mai 2025',
            status: 'scheduled',
            sent_count: 0,
            open_count: 0,
            click_count: 0,
            scheduled_date: '2025-05-25T08:00:00Z',
            created_at: '2025-05-10T14:20:00Z',
            target_audience: 'Tous les prospects'
          },
          {
            id: '3',
            name: 'Quiz Follow-up',
            subject: 'Votre score au quiz Smart Impact',
            status: 'draft',
            sent_count: 0,
            open_count: 0,
            click_count: 0,
            scheduled_date: null,
            created_at: '2025-05-18T09:45:00Z',
            target_audience: 'Prospects ayant fait le quiz'
          }
        ]);
      } catch (error) {
        console.error('Error fetching campaigns:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCampaigns();
  }, []);

  // Filter campaigns based on search term
  const filteredCampaigns = campaigns.filter(campaign => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      campaign.name.toLowerCase().includes(searchLower) || 
      campaign.subject.toLowerCase().includes(searchLower) ||
      campaign.target_audience.toLowerCase().includes(searchLower)
    );
  });

  // Pagination
  const indexOfLastCampaign = currentPage * campaignsPerPage;
  const indexOfFirstCampaign = indexOfLastCampaign - campaignsPerPage;
  const currentCampaigns = filteredCampaigns.slice(indexOfFirstCampaign, indexOfLastCampaign);
  const totalPages = Math.ceil(filteredCampaigns.length / campaignsPerPage);
  
  // Navigation
  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);
  const previousPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));
  const nextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));

  // Format date
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Status badge
  const StatusBadge = ({ status }: { status: Campaign['status'] }) => {
    let bgColor = 'bg-gray-100 text-gray-800';
    let statusText = 'Inconnu';
    
    switch (status) {
      case 'draft':
        bgColor = 'bg-gray-100 text-gray-800';
        statusText = 'Brouillon';
        break;
      case 'scheduled':
        bgColor = 'bg-blue-100 text-blue-800';
        statusText = 'Programmée';
        break;
      case 'sent':
        bgColor = 'bg-green-100 text-green-800';
        statusText = 'Envoyée';
        break;
      case 'paused':
        bgColor = 'bg-amber-100 text-amber-800';
        statusText = 'En pause';
        break;
    }
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bgColor}`}>
        {statusText}
      </span>
    );
  };

  // Handle creating a new campaign (simplified for now)
  const handleCreateCampaign = () => {
    // In a real app, this would send data to your backend
    const newCampaignData: Campaign = {
      id: `temp-${Date.now()}`,
      name: newCampaign.name,
      subject: newCampaign.subject,
      status: 'draft',
      sent_count: 0,
      open_count: 0,
      click_count: 0,
      scheduled_date: null,
      created_at: new Date().toISOString(),
      target_audience: newCampaign.target_audience
    };
    
    setCampaigns([newCampaignData, ...campaigns]);
    setShowCreateModal(false);
    setNewCampaign({
      name: '',
      subject: '',
      content: '',
      target_audience: 'all'
    });
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Campagnes</h1>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle Campagne
            </button>
          </div>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            {/* Search and Filters */}
            <div className="p-4 border-b flex justify-between items-center">
              <div className="flex gap-4">
                <button
                  className="px-4 py-2 rounded bg-blue-100 text-blue-800"
                >
                  Toutes
                </button>
                <button
                  className="px-4 py-2 rounded text-gray-600 hover:bg-gray-50"
                >
                  Brouillons
                </button>
                <button
                  className="px-4 py-2 rounded text-gray-600 hover:bg-gray-50"
                >
                  Programmées
                </button>
                <button
                  className="px-4 py-2 rounded text-gray-600 hover:bg-gray-50"
                >
                  Envoyées
                </button>
              </div>
              
              {/* Search input */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1); // Reset to first page on search
                  }}
                  placeholder="Rechercher une campagne..."
                  className="block w-64 pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
            </div>

            {/* Campaigns List */}
            {isLoading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
                <p className="mt-4 text-gray-500">Chargement des campagnes...</p>
              </div>
            ) : (
              <>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Campagne
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Audience
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Statut
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Performance
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentCampaigns.length > 0 ? (
                      currentCampaigns.map((campaign) => (
                        <tr key={campaign.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {campaign.name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {campaign.subject}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              <Users className="h-4 w-4 text-gray-400 mr-2" />
                              <span className="text-sm text-gray-500">
                                {campaign.target_audience}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <StatusBadge status={campaign.status} />
                          </td>
                          <td className="px-6 py-4">
                            {campaign.status === 'sent' ? (
                              <div className="space-y-1">
                                <div className="text-xs text-gray-500">
                                  <span className="font-medium">{campaign.sent_count}</span> envois
                                </div>
                                <div className="text-xs text-gray-500">
                                  <span className="font-medium">{Math.round((campaign.open_count / Math.max(campaign.sent_count, 1)) * 100)}%</span> ouvertures
                                </div>
                                <div className="text-xs text-gray-500">
                                  <span className="font-medium">{Math.round((campaign.click_count / Math.max(campaign.sent_count, 1)) * 100)}%</span> clics
                                </div>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center text-sm text-gray-500">
                              <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                              {campaign.status === 'scheduled' ? (
                                <span>Programmée: {formatDate(campaign.scheduled_date)}</span>
                              ) : (
                                <span>Créée: {formatDate(campaign.created_at)}</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-right text-sm font-medium">
                            <div className="flex space-x-2">
                              <button className="text-gray-500 hover:text-gray-700">
                                <Send className="h-4 w-4" />
                              </button>
                              <button className="text-blue-500 hover:text-blue-700">
                                <Mail className="h-4 w-4" />
                              </button>
                              <button className="text-gray-500 hover:text-gray-700">
                                <MoreHorizontal className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="px-6 py-10 text-center">
                          <p className="text-gray-500">
                            {searchTerm ? 'Aucune campagne trouvée pour cette recherche.' : 'Aucune campagne disponible.'}
                          </p>
                          {!searchTerm && (
                            <button
                              onClick={() => setShowCreateModal(true)}
                              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                              <Plus className="w-4 h-4 mr-2" />
                              Créer votre première campagne
                            </button>
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {/* Pagination */}
                {filteredCampaigns.length > campaignsPerPage && (
                  <div className="px-6 py-3 flex items-center justify-between border-t border-gray-200">
                    <div className="flex-1 flex justify-between sm:hidden">
                      <button
                        onClick={previousPage}
                        disabled={currentPage === 1}
                        className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                      >
                        Précédent
                      </button>
                      <button
                        onClick={nextPage}
                        disabled={currentPage === totalPages}
                        className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                      >
                        Suivant
                      </button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm text-gray-700">
                          Affichage de <span className="font-medium">{indexOfFirstCampaign + 1}</span> à{' '}
                          <span className="font-medium">
                            {Math.min(indexOfLastCampaign, filteredCampaigns.length)}
                          </span>{' '}
                          sur <span className="font-medium">{filteredCampaigns.length}</span> campagnes
                        </p>
                      </div>
                      <div>
                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                          <button
                            onClick={previousPage}
                            disabled={currentPage === 1}
                            className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`}
                          >
                            <span className="sr-only">Précédent</span>
                            <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                          </button>
                          
                          {Array.from({ length: Math.min(5, totalPages) }).map((_, i) => {
                            let pageNumber;
                            if (totalPages <= 5) {
                              pageNumber = i + 1;
                            } else if (currentPage <= 3) {
                              pageNumber = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                              pageNumber = totalPages - 4 + i;
                            } else {
                              pageNumber = currentPage - 2 + i;
                            }
                            
                            return (
                              <button
                                key={pageNumber}
                                onClick={() => paginate(pageNumber)}
                                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === pageNumber ? 'z-10 bg-blue-50 border-blue-500 text-blue-600' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'}`}
                              >
                                {pageNumber}
                              </button>
                            );
                          })}
                          
                          <button
                            onClick={nextPage}
                            disabled={currentPage === totalPages}
                            className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`}
                          >
                            <span className="sr-only">Suivant</span>
                            <ChevronRight className="h-5 w-5" aria-hidden="true" />
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Create Campaign Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h2 className="text-xl font-bold mb-4">Nouvelle Campagne</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom de la campagne
                </label>
                <input
                  type="text"
                  value={newCampaign.name}
                  onChange={(e) => setNewCampaign({...newCampaign, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="ex: Newsletter Mai 2025"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sujet de l'email
                </label>
                <input
                  type="text"
                  value={newCampaign.subject}
                  onChange={(e) => setNewCampaign({...newCampaign, subject: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="ex: Découvrez nos dernières actualités"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Audience cible
                </label>
                <select
                  value={newCampaign.target_audience}
                  onChange={(e) => setNewCampaign({...newCampaign, target_audience: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="all">Tous les prospects</option>
                  <option value="viewed">Pages vues uniquement</option>
                  <option value="quiz">Ayant fait le quiz</option>
                  <option value="highscore">Score élevé au quiz</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contenu de l'email
                </label>
                <textarea
                  value={newCampaign.content}
                  onChange={(e) => setNewCampaign({...newCampaign, content: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md h-32"
                  placeholder="Contenu de votre email..."
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleCreateCampaign}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                disabled={!newCampaign.name || !newCampaign.subject}
              >
                Créer
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default CampaignsPage;
